import React, { useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { formatWithSpace } from "../../lib/currency";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
);

const CHART_COLORS = [
  "#4f46e5",
  "#16a34a",
  "#0ea5e9",
  "#f97316",
  "#db2777",
  "#64748b",
  "#22c55e",
  "#e11d48",
  "#06b6d4",
  "#9333ea",
  "#f59e0b",
  "#14b8a6",
  "#ef4444",
  "#8b5cf6",
];

const THEME_COLORS = {
  light: {
    background: "#ffffff",
    text: "#0f172a",
    grid: "rgba(15, 23, 42, 0.08)",
    card: "#ffffff",
    border: "#e2e8f0",
  },
  dark: {
    background: "#0f172a",
    text: "#f8fafc",
    grid: "rgba(248, 250, 252, 0.12)",
    card: "#111827",
    border: "#1f2937",
  },
};

const truncateLabel = (value, max = 14) => {
  if (!value) return "";
  const text = String(value);
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
};

const groupTopN = (entries, topN, label = "Others") => {
  if (entries.length <= topN) return entries;
  const top = entries.slice(0, topN);
  const rest = entries.slice(topN);
  const restSum = rest.reduce((sum, [, v]) => sum + v, 0);
  return [...top, [label, restSum]];
};

const formatNumber = (value, language = "en") => {
  return Number(value).toLocaleString(
    language === "fr" ? "fr-FR" : "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const ConditionalChart = ({
  data = { rows: [], columns: [] },
  title = {},
  theme = "light",
  height = 360,
  width = "100%",
  showLegend = true,
  animate = true,
  language = "fr",
}) => {
  const chartRef = useRef(null);
  const { rows, columns } = data;
  const resolvedTitle = useMemo(() => {
    if (!title) return {};
    if (typeof title === "string") {
      try {
        return JSON.parse(title);
      } catch (error) {
        return {};
      }
    }
    console.log(title,"resolveTitle")
    return title;
  }, [title]);
  const colors = THEME_COLORS[theme] || THEME_COLORS.light;

  const chartConfig = useMemo(() => {
    if (!rows?.length || !columns?.length) {
      return { shouldRender: false, reason: "No data provided" };
    }

    const numericColumns = columns.filter((col) =>
      rows.some((row) => typeof row[col] === "number" && !Number.isNaN(row[col])),
    );

    const categoricalColumns = columns.filter(
      (col) =>
        !numericColumns.includes(col) &&
        rows.some((row) => row[col] !== null && row[col] !== undefined),
    );

    const numericField = resolvedTitle["Y-axis"] || numericColumns[0] || columns[0];
    const categoryField =
      resolvedTitle["X-axis"] ||
      categoricalColumns[0] ||
      columns.find((c) => c !== numericField) ||
      columns[0];

    // const sortedEntries = rows
    //   .map((row) => [
    //     row[categoryField] || "Unknown",
    //     Number(row[numericField] || 0),
    //   ])
    
    const sortedEntries = rows.map((row) => [
      row[categoryField] || "Unknown",
      Number(row[numericField] || 0),
    ]);

    const uniqueCategories = sortedEntries.length;
    const categoryLooksTemporal = /date|time/i.test(categoryField);

    const getTitleNumber = (candidates) => {
      if (!resolvedTitle) return Number.NaN;
      for (const key of candidates) {
        if (Object.prototype.hasOwnProperty.call(resolvedTitle, key)) {
          return Number(resolvedTitle[key]);
        }
      }
      const lowerKeys = Object.keys(resolvedTitle).reduce((acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      }, {});
      for (const key of candidates) {
        const matchedKey = lowerKeys[key.toLowerCase()];
        if (matchedKey) return Number(resolvedTitle[matchedKey]);
      }
      return Number.NaN;
    };

    const minRecords = getTitleNumber([
      "Minimum Records",
      "Minimum Record",
      "minimum_records",
      "min_records",
      "minRecords",
      "minRecord",
      "minimumRecords",
      "minimumRecord",
    ]);
    const maxRecords = getTitleNumber([
      "Maximum Records",
      "Maximum Record",
      "maximum_records",
      "max_records",
      "maxRecords",
      "maxRecord",
      "maximumRecords",
      "maximumRecord",
    ]);
    const hasExplicitMinMax = Number.isFinite(minRecords) && Number.isFinite(maxRecords);
    const enforceLineChart = hasExplicitMinMax
      ? minRecords === 2 && maxRecords === 5
      : rows.length >= 2 && rows.length <= 5;
    const enforcePieChart = hasExplicitMinMax
      ? minRecords === 6 && maxRecords === 10
      : rows.length >= 6 && rows.length <= 10;

    let selectedChartType = "bar";
    if (enforceLineChart) {
      selectedChartType = "line";
    } else if (enforcePieChart) {
      selectedChartType = "pie";
    } else if (uniqueCategories <= 10) {
      selectedChartType = "pie";
    // } else if (categoryLooksTemporal) {
    //   selectedChartType = "line";
    }

    // const isPie = selectedChartType === "pie";
    // const limitedEntries = isPie
    //   ? groupTopN(sortedEntries, 8)
    //   : sortedEntries.slice(0, 30);


    const isPie = selectedChartType === "pie";
    // const maxItems = isPie ? 8 : 30;
    // const limitedEntries = groupTopN(sortedEntries, maxItems);
    const limitedEntries = isPie
      // ? groupTopN(sortedEntries, 8)
      ? groupTopN(sortedEntries, 10)
      : sortedEntries.slice(0, 30);

    const labels = limitedEntries.map(([key]) => String(key));
    const values = limitedEntries.map(([, value]) => Number(value) || 0);
    const totalValue = values.reduce((a, b) => a + b, 0);

    const showDataLabels = labels.length <= 12;

    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: animate ? { duration: 700 } : false,
      layout: {
        padding: {
          top: 4,
          right: 4,
          bottom: 8,
          left: 4,
        },
      },
      plugins: {
        legend: {
          display: showLegend,
          position: isPie ? "right" : "top",
          align: isPie ? "center" : "center",
          labels: { color: colors.text },
        },
        tooltip: {
          backgroundColor: colors.card,
          titleColor: colors.text,
          bodyColor: colors.text,
          borderColor: colors.border,
          borderWidth: 1,
          callbacks: {
            title: (context) => context?.[0]?.label || "",
            label: (context) => {
              const value = context.raw;
              if (isPie) {
                const percentage = totalValue
                  ? ((value / totalValue) * 100).toFixed(1)
                  : 0;
                return `${context.label}: ${formatWithSpace(value, language)} (${percentage}%)`;
              }
              return `${context.dataset.label}: ${formatWithSpace(value, language)}`;
            },
          },
        },
        datalabels: showDataLabels
          ? {
              color: isPie ? "#ffffff" : colors.text,
              anchor: isPie ? "center" : "end",
              align: isPie ? "center" : "top",
              offset: isPie ? 0 : 4,
              font: { size: 10, weight: isPie ? "bold" : "normal" },
              formatter: (value) => {
                if (!isPie) return formatWithSpace(value, language);
                const percentage = totalValue
                  ? ((value / totalValue) * 100).toFixed(1)
                  : 0;
                return `${percentage}%`;
              },
            }
          : false,
      },
    };

    let config;

    if (selectedChartType === "pie") {
      config = {
        component: Pie,
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: CHART_COLORS.slice(0, labels.length),
              borderColor: colors.background,
              borderWidth: 2,
              hoverOffset: 8,
            },
          ],
        },
        options: {
          ...commonOptions,
          radius: "70%",
          cutout: "0%",
        },
      };
    } else if (selectedChartType === "line") {
      config = {
        component: Line,
        data: {
          labels,
          datasets: [
            {
              label: numericField,
              data: values,
              borderColor: CHART_COLORS[0],
              backgroundColor: "rgba(79, 70, 229, 0.16)",
              tension: 0.35,
              fill: true,
              pointBackgroundColor: CHART_COLORS[0],
              pointBorderColor: "#ffffff",
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 2,
            },
          ],
        },
        options: {
          ...commonOptions,
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: colors.grid },
              ticks: { color: colors.text },
            },
            x: {
              grid: { display: false },
              ticks: {
                color: colors.text,
                callback: (value, index) => truncateLabel(labels[index]),
              },
            },
          },
        },
      };
    } else {
      config = {
        component: Bar,
        data: {
          labels,
          datasets: [
            {
              label: numericField,
              data: values,
              backgroundColor: CHART_COLORS.slice(0, labels.length),
              borderRadius: 8,
              borderSkipped: false,
              barPercentage: 0.7,
              categoryPercentage: 0.72,
            },
          ],
        },
        options: {
          ...commonOptions,
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: colors.grid },
              ticks: { color: colors.text },
            },
            x: {
              grid: { display: false },
              ticks: {
                color: colors.text,
                maxRotation: 35,
                minRotation: 35,
                callback: (value, index) => truncateLabel(labels[index]),
              },
            },
          },
        },
      };
    }

    const minWidth = Math.max(560, labels.length * 56);

    return {
      shouldRender: true,
      chartType: selectedChartType,
      data: config.data,
      options: config.options,
      component: config.component,
      minWidth,
    };
  }, [rows, columns, resolvedTitle, theme, showLegend, animate]);

  if (chartConfig.chartType && resolvedTitle?.graph_Status === false) {
    return null;
  }

  if (!chartConfig.shouldRender) {
    return null;
  }

  const downloadPNG = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imgData;
    link.download = "report-chart.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ChartComponent = chartConfig.component;
  const isDark = theme === "dark";

  return (
    <div
      className={`flex h-full w-full flex-col rounded-2xl border p-3 shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
          Visualization
        </div>
        <button
          type="button"
          onClick={downloadPNG}
          className="rounded-full bg-[#cc0452] p-2 text-white transition hover:brightness-110"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-download"
          >
            <path d="M12 15V3" />
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="m7 10 5 5 5-5" />
          </svg>
        </button>
      </div>
      <div className="mt-2 flex-1 overflow-x-auto">
        <div
          ref={chartRef}
          className="h-full"
          style={{ width, minWidth: chartConfig.minWidth, height }}
        >
          <ChartComponent data={chartConfig.data} options={chartConfig.options} />
        </div>
      </div>
    </div>
  );
};

export default ConditionalChart;
